import Button from '@mui/material/Button'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import type { KeyboardEvent, ReactNode } from 'react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getNextPriceRowIndexFromKeyboardInput, isValidPriceValue } from '../IntradayTradingView.helpers'
import styles from '../IntradayTradingView.module.less'
import type { HourInput } from '../IntradayTradingView.types'

type PriceInputSectionProps = {
  inputPrices: HourInput[]
  priceHourLabels: string[]
  hasInvalidPriceInputs: boolean
  onPriceChange: (id: number, value: string) => void
  onRemoveLastRow: () => void
  onAddRow: () => void
}

export function PriceInputSection({
  inputPrices,
  priceHourLabels,
  hasInvalidPriceInputs,
  onPriceChange,
  onRemoveLastRow,
  onAddRow,
}: PriceInputSectionProps): ReactNode {
  const { t } = useTranslation()
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({})

  function setInputRef(rowIndex: number, node: HTMLInputElement | null): void {
    inputRefs.current[rowIndex] = node
  }

  function handlePriceCellKeyDown(event: KeyboardEvent<HTMLInputElement>, rowIndex: number): void {
    const targetRowIndex = getNextPriceRowIndexFromKeyboardInput({
      key: event.key,
      currentRowIndex: rowIndex,
      rowCount: inputPrices.length,
    })

    if (targetRowIndex === null) {
      return
    }

    event.preventDefault()

    if (targetRowIndex === rowIndex) {
      return
    }

    inputRefs.current[targetRowIndex]?.focus()
  }

  return (
    <div className={styles.IntradayTradingView_PriceInputPopoverContent}>
      <div className={styles.IntradayTradingView_PriceTableLayout}>
        <div className={styles.IntradayTradingView_PriceTableWrapper}>
          <table className={styles.IntradayTradingView_PriceTable}>
            <thead>
              <tr>
                <th className={styles.IntradayTradingView_PriceTableTh}>{t('Hour')}</th>
                <th className={styles.IntradayTradingView_PriceTableTh}>{t('Price (SEK/MWh)')}</th>
              </tr>
            </thead>
            <tbody>
              {inputPrices.map((price, index) => (
                <tr key={price.id} className={styles.IntradayTradingView_PriceTableRow}>
                  <td className={styles.IntradayTradingView_PriceTableLabelCell}>{priceHourLabels[index]}</td>
                  <td
                    className={`${styles.IntradayTradingView_PriceTableInputCell} ${
                      price.value.trim() !== '' && !isValidPriceValue(price.value)
                        ? styles.IntradayTradingView_PriceTableInputCellInvalid
                        : ''
                    }`}
                  >
                    <TextField
                      variant="standard"
                      type="text"
                      value={price.value}
                      onChange={(event) => onPriceChange(price.id, event.target.value)}
                      inputRef={(node) => setInputRef(index, node)}
                      fullWidth
                      error={price.value.trim() !== '' && !isValidPriceValue(price.value)}
                      slotProps={{
                        htmlInput: {
                          inputMode: 'decimal',
                          onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => handlePriceCellKeyDown(event, index),
                          'aria-label': t('Price input'),
                        },
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {inputPrices.length > 0 ? (
          <div className={styles.IntradayTradingView_PriceTableAction}>
            <IconButton
              onClick={onRemoveLastRow}
              aria-label={t('Remove last row')}
              size="small"
              tabIndex={-1}
              sx={{
                '& .MuiIcon-root': {
                  fontSize: '0.8rem',
                },
              }}
            >
              <Icon fontSize="inherit" className="fas fa-trash" color="error" />
            </IconButton>
          </div>
        ) : null}
      </div>

      {hasInvalidPriceInputs ? (
        <div className={styles.IntradayTradingView_PriceTableValidationMessage}>
          {t('Only numeric values are allowed in price cells (decimals are allowed).')}
        </div>
      ) : null}

      <div className={styles.IntradayTradingView_PriceTableActionRow}>
        <Button variant="text" size="small" onClick={onAddRow}>
          {t('Add more')}
        </Button>
      </div>
    </div>
  )
}
